'use client'

import { useMemo, useSyncExternalStore } from 'react'
import { useWorkspaceStore } from '@/stores/workspace-store'

// Hydration-safe boolean: false during SSR + the first client render,
// true thereafter. Uses useSyncExternalStore so there is no setState-in-
// effect (the server snapshot is the SSR value).
const noopSubscribe = () => () => {}
function useHydrated() {
  return useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false,
  )
}

/**
 * Progressive complexity hook.
 *
 * Returns boolean flags that indicate which UI features should be visible
 * based on the workspace/domain count.
 *
 * Use-case tiers:
 *   UC1 — single workspace, single domain   (simplest UI)
 *   UC2 — single workspace, multiple domains (show domain-aware features)
 *   UC3 — multiple workspaces               (show full enterprise UI)
 */
export function useFeatureVisibility() {
  const workspaces = useWorkspaceStore((s) => s.workspaces)

  // The workspace store rehydrates from a persisted client cache before the
  // first client render, so `workspaces.length` differs between SSR (default)
  // and the client. That divergence changes the nav tree and drifts Radix's
  // useId — a hydration mismatch on the shared sidebar. Hold the
  // SSR-equivalent value until mount, then switch to the real one as an
  // ordinary post-hydration update.
  const hydrated = useHydrated()

  return useMemo(() => {
    // Derive domain count from unique email domains across recipients
    // For now we use workspace count as proxy; domains will be enriched
    // once the backend exposes a domains list on the workspace payload.
    //
    // Gated on `hydrated`: the workspace store may rehydrate from a
    // persisted client cache, so `workspaces.length` can differ between
    // SSR and the first client render. Treating it as single-workspace
    // until mount keeps the SSR + first-render nav tree identical (the
    // Radix useId / hydration-mismatch fix).
    const multiWorkspace = hydrated && workspaces.length > 1

    return {
      // UC3: multiple workspaces
      showWorkspaceSelector: multiWorkspace,
      showCompanyLibrary: multiWorkspace,
      showBlueprintFeatures: multiWorkspace,
      showSettingsInheritance: multiWorkspace,
      showAllWorkspacesView: multiWorkspace,

      // UC2+: multiple domains (approximated by multi-workspace for now)
      showPersonColumn: multiWorkspace,

      // UC1: always show these
      showSmartGroups: true,
    }
  }, [workspaces.length, hydrated])
}
